import { getDistanceToFinish } from '../../../app/components/Tournaments/Widget/components/utils'
import { MembershipWithCurrentPlayer } from '../../../app/containers/Tournaments/types/Membership'
import { isServerDatePast } from '../../../app/utils/dates'
import trans from '../../trans'
import TournamentModel from './Model'
import { EVENT_NEW_DATA, EVENT_NEW_MEMBERSHIP_DATA, EVENT_TOURNAMENT_FOUND } from './Model/constants'
import { RenderProps } from './Model/types'
import TournamentTemplate from './Templates/Tournament'
import { INITIALIZE_DELAY } from './constants'
import { updateScoreBoard, getPosition, isInViewport, savePosition, show, toggleVisibility, hide } from './utils'

class Tournaments {
  tournamentModel!: TournamentModel
  wasMoving = false
  x = 0
  y = 0
  body = document.querySelector<HTMLBodyElement>('body')!
  $!: any
  finishDateIntervalId?: NodeJS.Timeout

  initialize() {
    setTimeout(() => {
      this.tournamentModel = new TournamentModel()
      this.initializeTournamentModelBindings()
    }, INITIALIZE_DELAY)
  }

  initializeTournamentModelBindings() {
    this.tournamentModel.addEventListener(EVENT_TOURNAMENT_FOUND, this.initializeDOM)
    this.tournamentModel.addEventListener(EVENT_NEW_DATA, ({ detail }: any) => this.render(detail))
    this.tournamentModel.addEventListener(EVENT_NEW_MEMBERSHIP_DATA, ({ detail: { memberships } }: any) =>
      this.renderScoreBoard(memberships),
    )
  }

  initializeDOM = () => {
    this.appendTournamentElement()
    this.$ = this.initializeDomElements()
    this.initializeDOMBindings()
    this.initDraggable()
  }

  appendTournamentElement() {
    const tournamentTemplate = new TournamentTemplate()
    this.body.prepend(tournamentTemplate.getHTMLElement())
  }

  initializeDomElements() {
    return {
      container: document.querySelector<HTMLElement>('[data-tournament="container"]'),
      toggle: document.querySelector<HTMLElement>('[data-tournament="toggle"]'),
      wrapper: document.querySelector<HTMLElement>('[data-tournament="wrapper"]'),
      tableContainer: document.querySelector<HTMLTableElement>('[data-tournament="tableContainer"]'),
      name: document.querySelector<HTMLElement>('[data-tournament="name"]'),
      finishDate: document.querySelector<HTMLElement>('[data-tournament="finishDate"]'),
      showMoreButton: document.querySelector<HTMLButtonElement>('[data-tournament="showMore"]'),
      close: document.querySelector<HTMLElement>('[data-tournament="close"]'),
      subscribeButton: document.querySelector<HTMLElement>('[data-tournament="subscribe"]'),
      getTbody: (document: Document) => document.querySelector<HTMLTableElement>('[data-tournament="tbody"]'),
      getGameObject: (document: Document) => document.querySelector<HTMLObjectElement>('#gamePlayOverlay'),
      isOpen: () => this.$.wrapper.style.display === 'block',
    }
  }

  initializeDOMBindings() {
    this.$.toggle.addEventListener('click', this.toggle)
    this.$.close.addEventListener('click', this.toggle)
    this.$.subscribeButton.addEventListener('click', this.subscribe)
  }

  open() {
    show(this.$.wrapper)
    this.togglerUpdate()
  }

  toggle = () => {
    if (!this.wasMoving) {
      toggleVisibility(this.$.wrapper, this.$.toggle)
    }

    this.togglerUpdate()
    this.wasMoving = false
  }

  subscribe = () => {
    this.tournamentModel.subscribe()
  }

  showSubscribeButton = () => {
    show(this.$.subscribeButton)
  }

  togglerUpdate() {
    if (!this.$.isOpen()) {
      this.$.toggle.classList.remove('closed')
    } else {
      this.$.toggle.classList.add('closed')
    }
  }

  initDraggable = () => {
    const position = getPosition()

    this.$.toggle.addEventListener('mousedown', this.mouseDownHandler)

    if (
      isInViewport({
        top: Number(position?.top),
        left: Number(position?.left),
        right: Number(position?.right),
        bottom: Number(position?.bottom),
      })
    ) {
      this.$.container.style.top = `${position.top}px`
      this.$.container.style.left = `${position.left}px`
    }
  }

  mouseDownHandler = (e: MouseEvent) => {
    this.x = e.clientX
    this.y = e.clientY

    this.$.getGameObject(document).style.pointerEvents = 'none'

    window.addEventListener('mousemove', this.mouseMoveHandler)
    window.addEventListener('mouseup', this.mouseUpHandler)
  }

  mouseMoveHandler = (e: MouseEvent) => {
    this.wasMoving = true
    this.open()

    const dx = e.clientX - this.x
    const dy = e.clientY - this.y
    const top = this.$.container.offsetTop + dy
    const left = this.$.container.offsetLeft + dx
    const { right, bottom } = this.$.container.getBoundingClientRect()

    if (isInViewport({ top, left, right: right + dx, bottom: bottom + dy })) {
      this.$.container.style.top = `${top}px`
      this.$.container.style.left = `${left}px`

      this.x = e.clientX
      this.y = e.clientY
    }
  }

  mouseUpHandler = () => {
    this.$.getGameObject(document).style.pointerEvents = 'inherit'

    window.removeEventListener('mousemove', this.mouseMoveHandler)
    window.removeEventListener('mouseup', this.mouseUpHandler)
    savePosition(this.$.container)
  }

  renderName(name: string) {
    this.$.name.textContent = name
  }

  renderEndDate(finishDate: Date) {
    const tournamentEnded = isServerDatePast(finishDate)

    if (tournamentEnded) {
      clearInterval(this.finishDateIntervalId)
      this.finishDateIntervalId = undefined
      this.$.finishDate.parentElement.innerText = trans('archivalTournament')
    }

    if (!this.finishDateIntervalId) {
      this.finishDateIntervalId = setInterval(() => {
        if (isServerDatePast(finishDate)) {
          this.$.finishDate.parentElement.innerText = trans('archivalTournament')
        } else {
          this.$.finishDate.textContent = getDistanceToFinish(finishDate)
        }
      }, 1000)
    }
  }

  renderScoreBoard(memberships: MembershipWithCurrentPlayer[]) {
    updateScoreBoard(this.$.getTbody(document), memberships)
  }

  renderSubscribeButton(isSubscribed: boolean, isSubscriptionPermitted: boolean) {
    if (isSubscribed || !isSubscriptionPermitted) {
      hide(this.$.subscribeButton)
    }
  }

  render = ({ isSubscriptionPermitted, tournament: { name, finishDate, isSubscribed } }: RenderProps) => {
    this.renderSubscribeButton(isSubscribed, isSubscriptionPermitted)
    this.renderName(name)
    this.renderEndDate(finishDate.date)
    show(this.$.container)
  }
}

export default Tournaments
